import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Plus, MapPin, Pencil, CalendarDays, Users2, Search, AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { BottomSheet, Empty, DeleteButton, Field, toISO, fmtData, BulkCheck, BulkBar, ConfirmAction, GIORNI_SETTIMANA } from "@/components/Shared";
import { useRefreshOnPull } from "@/components/PullToRefresh";

// Cerca dinamica su una lista di persone, mostra un massimo di N risultati
function AnimatoriSearchPicker({ animatori, selected, onToggle, testPrefix, max = 30 }) {
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const filtered = query
    ? animatori.filter((a) => `${a.nome} ${a.cognome} ${a.username}`.toLowerCase().includes(query))
    : animatori.filter((a) => selected.includes(a.id));
  const shown = filtered.slice(0, max);

  return (
    <div>
      <div className="relative mb-3">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          type="search"
          className="input-app !pl-11 !py-2.5"
          placeholder="Cerca animatore per nome, cognome o nome utente..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          // Questo campo vive DENTRO il form di assegnazione: senza questo,
          // premere Invio per cercare salverebbe l'assegnazione per sbaglio.
          onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
          data-testid={`${testPrefix}-search-input`}
        />
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {selected.map((id) => {
            const u = animatori.find((a) => a.id === id);
            if (!u) return null;
            return (
              <span key={id} className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 px-3 py-1 text-xs font-bold" data-testid={`${testPrefix}-chip-${id}`}>
                {u.nome} {u.cognome}
                <button type="button" onClick={() => onToggle(id)} className="hover:text-amber-900" aria-label="Rimuovi" data-testid={`${testPrefix}-remove-${id}`}>×</button>
              </span>
            );
          })}
        </div>
      )}
      {query ? (
        <div className="max-h-52 overflow-y-auto space-y-1 border border-slate-100 rounded-2xl p-1.5">
          {shown.length === 0 ? (
            <p className="text-xs text-slate-400 font-semibold text-center py-4">Nessun animatore trovato</p>
          ) : shown.map((a) => {
            const isSel = selected.includes(a.id);
            return (
              <button key={a.id} type="button" onClick={() => onToggle(a.id)}
                className={`w-full text-left rounded-xl px-3 py-2 text-sm font-semibold flex items-center justify-between transition-colors ${isSel ? "bg-amber-50 text-amber-800" : "hover:bg-slate-50 text-slate-700"}`}
                data-testid={`${testPrefix}-item-${a.id}`}>
                <span>{a.nome} {a.cognome}</span>
                <span className={`text-xs ${a.role === "responsabile" ? "text-emerald-600" : a.role === "animatore" ? "text-amber-500" : "text-sky-500"}`}>
                  {a.role === "responsabile" ? "Responsabile" : a.role === "animatore" ? "Animatore" : "Aiuto"}
                </span>
              </button>
            );
          })}
          {filtered.length > shown.length && (
            <p className="text-[10px] text-slate-400 text-center py-1 font-semibold">…e altri {filtered.length - shown.length}. Restringi la ricerca.</p>
          )}
        </div>
      ) : (
        selected.length === 0 && (
          <p className="text-xs text-slate-400 font-semibold text-center py-3">Inizia a digitare per cercare un animatore</p>
        )
      )}
    </div>
  );
}

export default function Postazioni() {
  const [data, setData] = useState(toISO(new Date()));
  const [postazioni, setPostazioni] = useState([]);
  const [giochi, setGiochi] = useState([]);
  const [staff, setStaff] = useState([]);
  const [giorniAttivi, setGiorniAttivi] = useState([0, 1, 2, 3, 4]);
  const [periodoInizio, setPeriodoInizio] = useState("");
  const [periodoFine, setPeriodoFine] = useState("");

  const [nuovaOpen, setNuovaOpen] = useState(false);
  const [nuovaForm, setNuovaForm] = useState({ nome: "", gioco_id: "" });

  const [editAnag, setEditAnag] = useState(null);
  const [assegnaOpen, setAssegnaOpen] = useState(null);
  const [assegnaForm, setAssegnaForm] = useState({ animatori_ids: [], gioco_id: "" });
  const [saving, setSaving] = useState(false);
  const [punteggi, setPunteggi] = useState({});

  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState([]);

  const load = useCallback(() => {
    api.get(`/postazioni?data=${data}`).then((r) => setPostazioni(r.data));
    api.get("/giochi").then((r) => setGiochi(r.data));
    api.get("/utenti?role=responsabile,animatore,aiuto_animatore").then((r) => setStaff(r.data));
    api.get("/impostazioni").then((r) => {
      setGiorniAttivi(r.data.giorni_attivi || []);
      setPeriodoInizio(r.data.periodo_inizio || "");
      setPeriodoFine(r.data.periodo_fine || "");
    });
  }, [data]);

  useEffect(() => { load(); }, [load]);
  useRefreshOnPull(load);

  // Weekday JS: 0=Dom, 1=Lun... — normalizziamo su Lun=0 come nel resto dell'app
  const weekdayApp = (() => {
    const d = new Date(data + "T12:00:00");
    return (d.getDay() + 6) % 7;
  })();
  const nelPeriodo = !!periodoInizio && !!periodoFine && data >= periodoInizio && data <= periodoFine;
  const dataAttiva = nelPeriodo && giorniAttivi.includes(weekdayApp);
  const nomeGiorno = GIORNI_SETTIMANA[weekdayApp];

  const shiftGiornoAttivo = (dir) => {
    if (giorniAttivi.length === 0 || !periodoInizio || !periodoFine) return;
    const d = new Date(data + "T12:00:00");
    for (let i = 0; i < 14; i++) {
      d.setDate(d.getDate() + dir);
      const iso = toISO(d);
      if (iso < periodoInizio || iso > periodoFine) continue;
      const wd = (d.getDay() + 6) % 7;
      if (giorniAttivi.includes(wd)) {
        setData(iso);
        return;
      }
    }
  };

  const toggleAnim = (id) =>
    setAssegnaForm((f) => ({ ...f, animatori_ids: f.animatori_ids.includes(id) ? f.animatori_ids.filter((x) => x !== id) : [...f.animatori_ids, id] }));

  const creaPostazione = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/postazioni", { nome: nuovaForm.nome, gioco_id: nuovaForm.gioco_id || null });
      toast.success("Postazione creata");
      setNuovaOpen(false);
      setNuovaForm({ nome: "", gioco_id: "" });
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const salvaAnag = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/postazioni/${editAnag.id}`, { nome: editAnag.nome, gioco_id: editAnag.gioco_id || null });
      toast.success("Postazione aggiornata");
      setEditAnag(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const eliminaPostazione = async (id) => {
    try {
      await api.delete(`/postazioni/${id}`);
      toast.success("Postazione eliminata (anche i risultati collegati)");
      setEditAnag(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const apriAssegnazione = (p) => {
    if (!dataAttiva) return toast.error("Questo giorno non rientra nel periodo o nei giorni attivi dell'oratorio");
    setAssegnaOpen(p);
    setAssegnaForm({
      animatori_ids: p.animatori_ids || [],
      gioco_id: p.gioco_id_giorno || "",
    });
  };

  const salvaAssegnazione = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/postazioni/${assegnaOpen.id}/assegnazione?data=${data}`, {
        animatori_ids: assegnaForm.animatori_ids,
        gioco_id: assegnaForm.gioco_id || null,
      });
      toast.success(`Assegnazione del ${fmtData(data)} salvata`);
      setAssegnaOpen(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const svuotaGiornata = async () => {
    setSaving(true);
    try {
      await api.put(`/postazioni/${assegnaOpen.id}/assegnazione?data=${data}`, { animatori_ids: [], gioco_id: null });
      toast.success("Assegnazione della giornata rimossa");
      setAssegnaOpen(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const salvaPunteggio = async (p, squadraId) => {
    if (!dataAttiva) return toast.error("Puoi registrare punteggi solo durante il periodo e nei giorni attivi dell'oratorio");
    const key = `${p.id}:${squadraId}`;
    const val = punteggi[key];
    if (val === undefined || val === "") return;
    const esistente = p.risultati.find((r) => r.squadra_id === squadraId);
    try {
      if (esistente) await api.put(`/risultati/${esistente.id}`, { squadra_id: squadraId, punteggio: Number(val) });
      else await api.post(`/postazioni/${p.id}/risultati?data=${data}`, { squadra_id: squadraId, punteggio: Number(val) });
      toast.success("Punteggio salvato");
      setPunteggi((s) => ({ ...s, [key]: "" }));
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const selectAll = () => setSelected(selected.length === postazioni.length ? [] : postazioni.map((p) => p.id));
  const exitBulk = () => { setBulkMode(false); setSelected([]); };
  const bulkDelete = async () => {
    try {
      const { data: r } = await api.post("/postazioni/bulk/delete", { ids: selected });
      toast.success(`${r.deleted} postazioni eliminate`);
      exitBulk();
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const assegnate = useMemo(() => postazioni.filter((p) => p.animatori_ids?.length), [postazioni]);
  const nonAssegnate = useMemo(() => postazioni.filter((p) => !(p.animatori_ids?.length)), [postazioni]);

  return (
    <div data-testid="postazioni-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">Postazioni</h1>
          <p className="text-sm text-slate-500 font-semibold">Postazioni permanenti · scegli una giornata operativa per gestire il presidio</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {postazioni.length > 0 && (
            <button className={bulkMode ? "btn-secondary" : "btn-ghost"} onClick={() => bulkMode ? exitBulk() : setBulkMode(true)} data-testid="postazioni-bulk-toggle">
              {bulkMode ? "Annulla selezione" : "Seleziona"}
            </button>
          )}
          <button className="btn-primary" onClick={() => setNuovaOpen(true)} data-testid="nuova-postazione-btn">
            <Plus className="h-4 w-4" /> Nuova postazione
          </button>
        </div>
      </div>

      {bulkMode && (
        <BulkBar count={selected.length} total={postazioni.length} onSelectAll={selectAll} onClear={exitBulk} testId="postazioni-bulk-bar"
          actions={
            <ConfirmAction onConfirm={bulkDelete} title={`Eliminare ${selected.length} postazioni?`} description="Verranno eliminati anche tutti i risultati collegati." testId="postazioni-bulk-delete-confirm">
              <button type="button" disabled={selected.length === 0} className="btn-danger disabled:opacity-40 !py-2" data-testid="postazioni-bulk-delete-btn">
                Elimina {selected.length > 0 && `(${selected.length})`}
              </button>
            </ConfirmAction>
          } />
      )}

      <div className={`card-app p-4 mb-5 flex items-center gap-3 flex-wrap ${!dataAttiva ? "border border-red-200 bg-red-50/40" : ""}`}>
        <CalendarDays className="h-5 w-5 text-amber-500 shrink-0" />
        <p className="text-sm font-bold text-slate-700 shrink-0">Giornata:</p>
        <div className="flex items-center gap-2 flex-1 min-w-[230px]">
          <button type="button" className="btn-ghost !p-1.5 shrink-0" onClick={() => shiftGiornoAttivo(-1)} data-testid="postazioni-data-prev" aria-label="Giorno attivo precedente">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <input type="date" className="input-app !py-2 flex-1 min-w-0 sm:max-w-[180px]" value={data} onChange={(e) => setData(e.target.value)} data-testid="postazioni-data-input" />
          <button type="button" className="btn-ghost !p-1.5 shrink-0" onClick={() => shiftGiornoAttivo(1)} data-testid="postazioni-data-next" aria-label="Giorno attivo successivo">
            <ChevronRight className="h-4 w-4" />
          </button>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400 shrink-0">{nomeGiorno}</p>
        </div>
        {!dataAttiva && (
          <p className="text-xs font-bold text-red-600 flex items-center gap-1 basis-full" data-testid="postazioni-data-warning">
            <AlertTriangle className="h-3.5 w-3.5" />
            {"Questo giorno non rientra nel periodo o nei giorni attivi dell'oratorio: modifica le Impostazioni o scegli un'altra data."}
          </p>
        )}
      </div>

      {postazioni.length === 0 ? (
        <Empty icon={MapPin} text="Nessuna postazione creata" sub="Crea la prima postazione: sarà disponibile ogni giorno per essere assegnata" />
      ) : (
        <div className="space-y-4">
          {assegnate.map((p) => (
            <PostazioneCard key={p.id} p={p} punteggi={punteggi} setPunteggi={setPunteggi}
              bulkMode={bulkMode} selected={selected.includes(p.id)} onToggleSel={() => toggleSel(p.id)}
              dataAttiva={dataAttiva}
              onModificaAnag={() => setEditAnag({ id: p.id, nome: p.nome, gioco_id: p.gioco_id_default || "" })}
              onAssegna={() => apriAssegnazione(p)}
              onSalvaPunteggio={salvaPunteggio}
            />
          ))}
          {nonAssegnate.length > 0 && (
            <div>
              <p className="label-app mt-6">Postazioni non ancora assegnate a questa data</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {nonAssegnate.map((p) => (
                  <div key={p.id} className={`card-app p-4 ${bulkMode && selected.includes(p.id) ? "ring-2 ring-amber-400" : ""}`} data-testid={`postazione-libera-${p.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      {bulkMode && <BulkCheck checked={selected.includes(p.id)} onClick={() => toggleSel(p.id)} testId={`postazione-check-${p.id}`} />}
                      <div className="min-w-0 flex-1">
                        <p className="font-display font-extrabold text-slate-900 truncate">{p.nome}</p>
                        <p className="text-xs text-slate-400 font-semibold truncate">
                          Gioco default: {p.gioco_nome || "—"}
                        </p>
                      </div>
                      {!bulkMode && (
                        <button className="btn-ghost !p-1.5" onClick={() => setEditAnag({ id: p.id, nome: p.nome, gioco_id: p.gioco_id_default || "" })} data-testid={`modifica-anag-${p.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    {!bulkMode && (
                      <button className="btn-secondary !py-2 mt-3 w-full text-xs" disabled={!dataAttiva} onClick={() => apriAssegnazione(p)} data-testid={`assegna-${p.id}`}>
                        Assegna a questa giornata
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* NUOVA POSTAZIONE */}
      <BottomSheet open={nuovaOpen} onOpenChange={setNuovaOpen} title="Nuova postazione" description="Postazione permanente, disponibile ogni giornata attiva" testId="nuova-postazione-sheet">
        <form onSubmit={creaPostazione}>
          <Field label="Nome postazione">
            <input className="input-app" required value={nuovaForm.nome} onChange={(e) => setNuovaForm({ ...nuovaForm, nome: e.target.value })} placeholder="es. Campo basket" data-testid="postazione-nome-input" />
          </Field>
          <Field label="Gioco di default (facoltativo)" hint="Potrà essere sovrascritto giorno per giorno">
            <select className="input-app" value={nuovaForm.gioco_id} onChange={(e) => setNuovaForm({ ...nuovaForm, gioco_id: e.target.value })} data-testid="postazione-gioco-select">
              <option value="">Nessuno</option>
              {giochi.map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}
            </select>
          </Field>
          <button className="btn-primary w-full mt-2" disabled={saving} data-testid="postazione-submit-btn">
            {saving ? "Creazione..." : "Crea postazione"}
          </button>
        </form>
      </BottomSheet>

      {/* MODIFICA ANAGRAFICA */}
      <BottomSheet open={!!editAnag} onOpenChange={(v) => !v && setEditAnag(null)} title="Modifica postazione" testId="modifica-postazione-sheet">
        {editAnag && (
          <form onSubmit={salvaAnag}>
            <Field label="Nome postazione">
              <input className="input-app" required value={editAnag.nome} onChange={(e) => setEditAnag({ ...editAnag, nome: e.target.value })} data-testid="edit-postazione-nome" />
            </Field>
            <Field label="Gioco di default">
              <select className="input-app" value={editAnag.gioco_id} onChange={(e) => setEditAnag({ ...editAnag, gioco_id: e.target.value })} data-testid="edit-postazione-gioco">
                <option value="">Nessuno</option>
                {giochi.map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}
              </select>
            </Field>
            <button className="btn-primary w-full mb-3" disabled={saving} data-testid="edit-postazione-salva">
              {saving ? "Salvataggio..." : "Salva modifiche"}
            </button>
            <div className="flex justify-end">
              <DeleteButton onConfirm={() => eliminaPostazione(editAnag.id)} entity={`la postazione ${editAnag.nome} e tutti i suoi punteggi`} testId="elimina-postazione-btn" />
            </div>
          </form>
        )}
      </BottomSheet>

      {/* ASSEGNAZIONE GIORNALIERA */}
      <BottomSheet open={!!assegnaOpen} onOpenChange={(v) => !v && setAssegnaOpen(null)} title={assegnaOpen ? `${assegnaOpen.nome} · ${fmtData(data)}` : ""} description="Assegna gli animatori/aiuti che presidiano oggi e (facoltativo) un gioco diverso dal default. Tutte le squadre giocheranno a questa postazione." testId="assegna-postazione-sheet">
        {assegnaOpen && (
          <form onSubmit={salvaAssegnazione}>
            <Field label="Gioco per questa giornata" hint={assegnaOpen.gioco_id_default ? `Default: ${assegnaOpen.gioco_nome || "—"}` : "Nessun gioco di default"}>
              <select className="input-app" value={assegnaForm.gioco_id} onChange={(e) => setAssegnaForm({ ...assegnaForm, gioco_id: e.target.value })} data-testid="assegna-gioco-select">
                <option value="">Usa il default</option>
                {giochi.map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}
              </select>
            </Field>
            <Field label={<span className="flex items-center gap-1"><Users2 className="h-3.5 w-3.5" /> Animatori di presidio</span>}>
              <AnimatoriSearchPicker animatori={staff} selected={assegnaForm.animatori_ids} onToggle={toggleAnim} testPrefix="presidio" />
            </Field>
            <button className="btn-primary w-full mb-2" disabled={saving} data-testid="assegna-submit-btn">
              {saving ? "Salvataggio..." : "Salva assegnazione"}
            </button>
            <button type="button" className="btn-ghost w-full text-xs !text-red-500" onClick={svuotaGiornata} data-testid="svuota-giornata-btn">
              Rimuovi assegnazione di questa giornata
            </button>
          </form>
        )}
      </BottomSheet>
    </div>
  );
}

function PostazioneCard({ p, punteggi, setPunteggi, onModificaAnag, onAssegna, onSalvaPunteggio, bulkMode, selected, onToggleSel, dataAttiva }) {
  return (
    <div className={`card-app p-5 ${bulkMode && selected ? "ring-2 ring-amber-400" : ""}`} data-testid={`postazione-card-${p.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
        <div className="flex items-start gap-3 min-w-0 sm:flex-1">
          {bulkMode && <BulkCheck checked={selected} onClick={onToggleSel} testId={`postazione-check-${p.id}`} />}
          <div className="min-w-0">
            <h2 className="font-display font-extrabold text-slate-900 text-lg break-words">{p.nome}</h2>
            <p className="text-xs text-slate-400 font-semibold">
              Gioco: {p.gioco_nome || "non assegnato"} · Presidio: {p.animatori.map((a) => `${a.nome} ${a.cognome}`).join(", ") || "nessuno"}
            </p>
          </div>
        </div>
        {!bulkMode && (
          <div className="flex flex-wrap gap-2 shrink-0">
            <button className="btn-ghost !py-2 text-xs" onClick={onModificaAnag} data-testid={`modifica-anag-${p.id}`}>
              <Pencil className="h-3.5 w-3.5" /> Rinomina
            </button>
            <button className="btn-secondary !py-2 text-xs" disabled={!dataAttiva} onClick={onAssegna} data-testid={`assegna-${p.id}`}>
              Modifica assegnazione
            </button>
          </div>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {p.squadre.map((s) => {
          const ris = p.risultati.find((r) => r.squadra_id === s.id);
          const key = `${p.id}:${s.id}`;
          return (
            <div key={s.id} className="rounded-2xl border border-slate-100 bg-slate-50/50 p-3 flex items-center gap-3" data-testid={`postazione-squadra-${p.id}-${s.id}`}>
              <span className="h-3 w-3 rounded-full shrink-0" style={{ background: s.colore || "#94a3b8" }} />
              <span className="font-bold text-slate-700 text-sm flex-1 truncate">{s.nome}</span>
              {ris && <span className="badge-info shrink-0">{ris.punteggio_grezzo}{ris.corretto_at ? " (corretto)" : ""}</span>}
              <input type="number" className="input-app !w-20 !py-1.5 text-center" placeholder={ris ? String(ris.punteggio_grezzo) : "—"}
                value={punteggi[key] ?? ""} onChange={(e) => setPunteggi((st) => ({ ...st, [key]: e.target.value }))}
                disabled={!dataAttiva} data-testid={`punteggio-input-${p.id}-${s.id}`} />
              <button className="btn-secondary !py-1.5 !px-3 text-xs shrink-0" disabled={!dataAttiva} onClick={() => onSalvaPunteggio(p, s.id)} data-testid={`punteggio-salva-${p.id}-${s.id}`}>OK</button>
            </div>
          );
        })}
        {p.squadre.length === 0 && <p className="text-xs text-slate-400 font-semibold">Nessuna squadra ancora creata</p>}
      </div>
    </div>
  );
}
